import { formatMoney, formatNumber, formatPercent } from "./licensingTheme";
import { useCountUp } from "./licensingMotion";

/**
 * The six numbers a platform owner checks first, and every one of them opens
 * the people behind it.
 *
 * A KPI you cannot act on is decoration: clicking "12 at risk" has to produce
 * the twelve names, or the tile is just a number to worry about.
 */
function Kpi({ label, value, detail, tone = "", onClick, actionLabel, loading }) {
  const text = useCountUp(loading ? 0 : value.amount, { format: value.format });
  const Element = onClick ? "button" : "div";

  return (
    <Element
      type={onClick ? "button" : undefined}
      className={`mcap-lic-kpi ${tone ? `is-${tone}` : ""} ${onClick ? "is-clickable" : ""}`}
      onClick={onClick}
      // The accessible name is the real figure from the first frame — a screen
      // reader must never be read a number that is still counting.
      aria-label={onClick ? `${label}: ${value.format(value.amount)}. ${actionLabel || "Open the list"}` : undefined}
    >
      <span className="mcap-lic-kpi-label">{label}</span>
      <span className="mcap-lic-kpi-value">{loading ? "—" : text}</span>
      <span className="mcap-lic-kpi-detail">{loading ? " " : detail}</span>
    </Element>
  );
}

export default function LicensingHero({ overview, loading, onDrill }) {

  const kpis = overview?.kpis ?? {};
  const risk = overview?.risk ?? {};
  const currency = overview?.currency ?? "USD";
  const revenue = Object.values(overview?.revenueByCurrency ?? {})[0] ?? {};
  const collected = (revenue.newCents ?? 0) + (revenue.renewalCents ?? 0) + (revenue.refundCents ?? 0);
  const atRisk =
    (risk.grace ?? 0) + (risk.cancelling30d ?? 0) + (risk.paidNoRecord ?? 0) + (risk.renewalOverdue ?? 0);

  const money = (cents) => formatMoney(cents, currency, { compact: true });

  return (
    <div className="mcap-lic-hero">
      <Kpi
        label="Monthly revenue"
        value={{ amount: kpis.mrrCents ?? 0, format: money }}
        detail={
          kpis.mrrUnpriced
            ? `Includes ${formatNumber(kpis.mrrUnpriced)} at list price (no payment on file)`
            : "Yearly plans counted as 1/12 per month"
        }
        tone="money"
        loading={loading}
        actionLabel="Open the paying teachers"
        onClick={() => onDrill({ type: "cohort", title: "Paying teachers", filters: { billingState: "paying" } })}
      />
      <Kpi
        label="Paying teachers"
        value={{ amount: kpis.payingTeachers ?? 0, format: formatNumber }}
        detail={`${formatNumber(kpis.monthly ?? 0)} monthly · ${formatNumber(kpis.yearly ?? 0)} yearly`}
        loading={loading}
        actionLabel="Open the paying teachers"
        onClick={() => onDrill({ type: "cohort", title: "Paying teachers", filters: { billingState: "paying" } })}
      />
      <Kpi
        label="On free trial"
        value={{ amount: kpis.onTrial ?? 0, format: formatNumber }}
        detail="14-day free trial"
        loading={loading}
        actionLabel="Open the live trials"
        onClick={() => onDrill({ type: "cohort", title: "On free trial", filters: { trialState: "trial_live" } })}
      />
      <Kpi
        label="Trial → paid"
        value={{ amount: (kpis.conversionRate ?? 0) * 100, format: (n) => (kpis.cohortCount ? `${Math.round(n * 10) / 10}%` : "—") }}
        detail={
          kpis.cohortCount
            ? `${formatNumber(kpis.convertedCount ?? 0)} of ${formatNumber(kpis.cohortCount)} ended trials paid`
            : "No trials ended in these dates"
        }
        loading={loading}
        actionLabel="Open the list"
        onClick={() =>
          onDrill({
            type: "cohort",
            title: "Trials that ended in these dates",
            filters: { trialEndedInRange: "true" },
          })
        }
      />
      <Kpi
        label="Money in"
        value={{ amount: collected, format: money }}
        detail={`After refunds${revenue.refundCents ? ` (${money(revenue.refundCents)} refunded)` : ""}`}
        tone="money"
        loading={loading}
        actionLabel="Open the payments"
        onClick={() => onDrill({ type: "payments", title: "Payments in these dates", filters: {} })}
      />
      <Kpi
        label="Needs attention"
        value={{ amount: atRisk, format: formatNumber }}
        detail={`${formatNumber(risk.grace ?? 0)} failed · ${formatNumber(risk.cancelling30d ?? 0)} ending · ${formatNumber(risk.paidNoRecord ?? 0)} no payment on file`}
        tone="risk"
        loading={loading}
        actionLabel="Open the list"
        onClick={() => onDrill({ type: "risk" })}
      />
    </div>
  );
}

export { formatPercent };
